import { useState } from 'react';
import { RefreshCcw } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';

/**
 * Shared "Refresh Stripe status" button used anywhere the admin wants to
 * force-poll Stripe for a record stuck in "Payment Pending" (ACH settling,
 * webhook drops, stale sessions, etc.).
 *
 * Handles the full recovery flow:
 *   1. POST /admin/payments/sync with {entity_type, entity_id}.
 *   2. On success → toast with outcome + refresh the parent list.
 *   3. On 409 stripe_session_missing → confirm dialog → re-POST with
 *      `clear_stale_session=true` so the record reverts to unpaid.
 *
 * Props:
 *   entityType: 'lane_booking' | 'tournament_registration' | 'payment_transaction'
 *   entityId:   string
 *   onUpdated:  optional callback after a successful sync / clear
 *   size:       'sm' | 'md' (default 'sm')
 *   label:      optional extra text next to the icon
 *   testId:     data-testid override
 */
function PaymentSyncButtonImpl({
  entityType,
  entityId,
  onUpdated,
  size = 'sm',
  label,
  testId,
  className = '',
  disabled = false,
}) {
  const [loading, setLoading] = useState(false);

  const clearStale = async (sessionId) => {
    try {
      const { data } = await api.post('/admin/payments/sync', {
        entity_type: entityType,
        entity_id: entityId,
        clear_stale_session: true,
      });
      toast.success(data?.hint || 'Stale Stripe session cleared.');
      onUpdated && onUpdated(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to clear stale session');
    }
  };

  const handleClick = async (e) => {
    e.stopPropagation();
    setLoading(true);
    try {
      const { data } = await api.post('/admin/payments/sync', {
        entity_type: entityType,
        entity_id: entityId,
      });
      const { outcome, hint, can_clear, stripe_payment_intent_status, stripe_payment_status } = data || {};
      const detail = hint || `Stripe: session=${stripe_payment_status || '—'}, PI=${stripe_payment_intent_status || '—'}`;
      if (outcome === 'succeeded') {
        toast.success(`Marked paid. ${detail}`);
      } else if (outcome === 'failed') {
        toast.warning(detail);  // already explains "Marked as Unpaid"
      } else if (outcome === 'processing') {
        toast.info(detail);
      } else if (outcome === 'open' && can_clear) {
        // Stripe session is still open — give the admin a one-click way to
        // revert the row to Unpaid and detach the open session, so they can
        // resend a fresh payment link instead of waiting for it to expire.
        const shouldClear = window.confirm(
          `${detail}\n\nClear the open Stripe session and revert this record to Unpaid?\n` +
          `You'll be able to send a fresh payment link.`
        );
        if (shouldClear) {
          await clearStale();
        }
      } else {
        toast.message(detail);
      }
      onUpdated && onUpdated(data);
    } catch (err) {
      const errDetail = err.response?.data?.detail;
      if (err.response?.status === 409 && errDetail && errDetail.code === 'stripe_session_missing') {
        const shouldClear = window.confirm(
          `${errDetail.message}\n\nSession ID: ${errDetail.session_id}\n\nClear this stuck session and reset to unpaid? ` +
          `You'll be able to re-send a payment link or mark it paid offline.`
        );
        if (shouldClear) await clearStale(errDetail.session_id);
      } else {
        const msg = typeof errDetail === 'string'
          ? errDetail
          : (errDetail?.message || 'Failed to sync with Stripe');
        toast.error(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  const iconSize = size === 'md' ? 'h-4 w-4' : 'h-3.5 w-3.5';
  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={disabled || loading}
      title="Refresh Stripe payment status"
      className={`inline-flex items-center gap-1 rounded p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${className}`}
      data-testid={testId || `sync-payment-${entityType}-${entityId}`}
    >
      <RefreshCcw className={`${iconSize} ${loading ? 'animate-spin' : ''}`} />
      {label && <span className="text-xs">{label}</span>}
    </button>
  );
}

// This control is intentionally limited by each calling screen to a
// Stripe-backed record and to admins. It is a recovery action, not a
// general status editor.
export default function PaymentSyncButton(props) {
  return <PaymentSyncButtonImpl {...props} />;
}
